import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import './Home.css';

// Partículas de cristal
function CrystalParticles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let w = canvas.width  = window.innerWidth;
    let h = canvas.height = window.innerHeight;
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h + h,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 0.6 + 0.2,
      opacity: Math.random() * 0.5 + 0.1,
      drift: (Math.random() - 0.5) * 0.3,
    }));

    const resize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      particles.forEach(p => {
        p.y  -= p.speed;
        p.x  += p.drift;
        if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(125,211,252,${p.opacity})`;
        ctx.shadowBlur = 6;
        ctx.shadowColor = '#7dd3fc';
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="particles-canvas" />;
}

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: [0.4, 0, 0.2, 1] } }) };

export default function Home() {
  const { data: stats } = useApi('/api/stats');
  const { data: progreso } = useApi('/api/progreso');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY    = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpac = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const pokedexPct = progreso
    ? Math.round((parseInt(progreso.pokedex) / 127) * 100) || 0
    : 0;

  return (
    <div className="home">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="hero" ref={heroRef}>
        <CrystalParticles />
        <motion.div className="hero-bg" style={{ y: heroY }} />
        <motion.div className="hero-content" style={{ opacity: heroOpac }}>
          <motion.p
            className="hero-eyebrow"
            variants={fadeUp} initial="hidden" animate="visible" custom={0}
          >
            Instituto Miravel · Proyecto Veltara
          </motion.p>
          <motion.h1
            className="hero-title"
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
          >
            La región donde<br />
            <span className="hero-title-crystal">el cristal recuerda</span>
          </motion.h1>
          <motion.p
            className="hero-subtitle"
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
          >
            Un fan-game Pokémon de época medieval. Dos Investigadores.<br />
            Un hilo temporal roto. 127 criaturas que no existen aún.
          </motion.p>
          <motion.div
            className="hero-ctas"
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
          >
            <Link to="/pokedex" className="cta-primary">Explorar Pokédex</Link>
            <Link to="/lore"    className="cta-secondary">Leer las Crónicas</Link>
          </motion.div>
        </motion.div>
        <div className="hero-scroll-hint">
          <span>◈</span>
          <span>Descender</span>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────── */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {[
              { label: 'Pokémon Registrados', value: stats?.pokemon ?? '—', icon: '📖', max: 127 },
              { label: 'Archivos de Lore',    value: stats?.lore    ?? '—', icon: '📜', max: null },
              { label: 'Sujetos Catalogados', value: stats?.personajes ?? '—', icon: '⚔️', max: null },
              { label: 'Activos Visuales',    value: stats?.arte    ?? '—', icon: '🖼️', max: null },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                className="stat-card crystal-card"
                variants={fadeUp} initial="hidden" whileInView="visible"
                viewport={{ once: true }} custom={i}
              >
                <span className="stat-icon">{s.icon}</span>
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
                {s.max && (
                  <div className="stat-progress">
                    <div className="progress-bar-track">
                      <motion.div
                        className="progress-bar-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${Math.round((s.value / s.max) * 100)}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, ease: [0.4, 0, 0.2, 1] }}
                      />
                    </div>
                    <span className="stat-progress-label">{s.value} / {s.max}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lore excerpt ─────────────────────────────────────── */}
      <section className="lore-excerpt">
        <div className="container">
          <div className="rune-divider"><span>◈ Sobre Veltara ◈</span></div>
          <div className="lore-two-col">
            <motion.div
              className="lore-text"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            >
              <h2>Una tierra de memoria rota</h2>
              <p>
                Antes de los humanos, Veltara era gobernada por dos entidades primordiales:
                <strong> Valdros</strong>, guardián del Pasado, y <strong>Miravel</strong>,
                visionaria del Futuro. Su equilibrio tejía el hilo temporal que daba coherencia al mundo.
              </p>
              <p>
                Hace diez mil años, ese hilo se deshilachó. De las grietas nació algo nuevo:
                el <span className="glow-text">Tipo Cristal</span>. Ahora, las Fallas de Resonancia
                se abren de nuevo, y algo despierta bajo la Montaña Sepulcro.
              </p>
              <Link to="/lore" className="cta-secondary" style={{ display: 'inline-flex', marginTop: '1.5rem' }}>
                Leer la historia completa →
              </Link>
            </motion.div>
            <motion.div
              className="lore-legend-box parchment-card"
              variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} custom={1}
            >
              <h3 className="legend-title">Calendario Veltariano</h3>
              <ul className="legend-list">
                <li><span className="legend-era">Era Cero</span><span>Antes de los humanos</span></li>
                <li><span className="legend-era">Era de los Clanes</span><span>Años 1–800 DLB</span></li>
                <li><span className="legend-era">Era de las Ciudades</span><span>Años 800–1.600 DLB</span></li>
                <li><span className="legend-era era-active">Era Moderna</span><span>Año 2.247 DLB ← Ahora</span></li>
              </ul>
              <p className="legend-note">DLB = Después de la Lluvia Blanca</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Quick links ──────────────────────────────────────── */}
      <section className="quick-links">
        <div className="container">
          <div className="rune-divider"><span>◈ Explorar ◈</span></div>
          <div className="quick-grid">
            {[
              { to: '/pokedex',    icon: '📖', title: 'Pokédex',    desc: 'Las criaturas de la región, como en un bloc de apuntes de época' },
              { to: '/personajes', icon: '⚔️',  title: 'Sujetos',   desc: 'Protagonistas, rivales y miembros de la Orden del Hilo' },
              { to: '/mapa',       icon: '🗺️',  title: 'Mapa',      desc: 'La geografía de Veltara: rutas, ciudades y Fallas de Resonancia' },
              { to: '/galeria',    icon: '🖼️',  title: 'Galería',   desc: 'Arte conceptual, sprites y activos visuales del proyecto' },
            ].map((q, i) => (
              <motion.div
                key={q.to}
                variants={fadeUp} initial="hidden" whileInView="visible"
                viewport={{ once: true }} custom={i}
              >
                <Link to={q.to} className="quick-card crystal-card">
                  <span className="quick-icon">{q.icon}</span>
                  <h3>{q.title}</h3>
                  <p>{q.desc}</p>
                  <span className="quick-arrow">→</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
